"use client";

import { useLayoutEffect, useRef, useState } from "react";
import Image from "next/image";
import parse from "html-react-parser";
import { IPageContent } from "@/types/pageContent.types";
import { motion } from "framer-motion";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import ReactMarkdown from "react-markdown";

import styles from "./styles.module.scss";
import TransitionLink from "@/components/TransitionLink";

const MotionImage = motion.create(Image);

gsap.registerPlugin(ScrollTrigger);

type AccordionSectionId = "section4" | "section5" | "section6";

export type IonHealthcareViewProps = {
  ionHealthcareSection1Data: IPageContent["ionHealthcarePageModel"]["ionHealthcarePage"]["ionHealthcareSection1"];
  ionHealthcareSection2Data: IPageContent["ionHealthcarePageModel"]["ionHealthcarePage"]["ionHealthcareSection2"];
  ionHealthcareSection3Data: IPageContent["ionHealthcarePageModel"]["ionHealthcarePage"]["ionHealthcareSection3"];
  ionHealthcareSection4Data: IPageContent["ionHealthcarePageModel"]["ionHealthcarePage"]["ionHealthcareSection4"];
  ionHealthcareSection5Data: IPageContent["ionHealthcarePageModel"]["ionHealthcarePage"]["ionHealthcareSection5"];
  ionHealthcareSection6Data: IPageContent["ionHealthcarePageModel"]["ionHealthcarePage"]["ionHealthcareSection6"];
  ionHealthcareSection7Data: IPageContent["ionHealthcarePageModel"]["ionHealthcarePage"]["ionHealthcareSection7"];
  ionHealthcareSection8Data: IPageContent["ionHealthcarePageModel"]["ionHealthcarePage"]["ionHealthcareSection8"];
  ionHealthcareSection9Data: IPageContent["ionHealthcarePageModel"]["ionHealthcarePage"]["ionHealthcareSection9"];
};


const HealthcareView: React.FC<IonHealthcareViewProps> = ({
  ionHealthcareSection1Data,
  ionHealthcareSection2Data,
  ionHealthcareSection3Data,
  ionHealthcareSection4Data,
  ionHealthcareSection5Data,
  ionHealthcareSection6Data,
  ionHealthcareSection7Data,
  ionHealthcareSection8Data,
  ionHealthcareSection9Data
}) => {
  const [faqActiveIndex, setFaqActiveIndex] = useState(0);
  const [stickyActiveIndex, setStickyActiveIndex] = useState(0);
  const [accordionActiveIndices, setAccordionActiveIndices] = useState<
    Record<AccordionSectionId, number | null>
  >({
    section4: null,
    section5: null,
    section6: null,
  });
  const stickyRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const stickyPanelsRef = useRef<Array<HTMLDivElement | null>>([]);
  const stickyStepsRef = useRef<Array<HTMLDivElement | null>>([]);
  const stickyCurrentPanelIndexRef = useRef(0);
  const stickyScrollTweenRef = useRef<gsap.core.Tween | null>(null);
  const stickyJumpTargetRef = useRef<number | null>(null);
  const stickyJumpingRef = useRef(false);
  const stickyJumpDelayTimerRef = useRef<number | null>(null);
  const section3Ref = useRef<HTMLElement>(null);
  const section9Ref = useRef<HTMLElement>(null);
  const MotionTransitionLink = motion.create(TransitionLink);

  const section4HeadingData = ionHealthcareSection4Data[0] as {
    heading: string;
    body: string;
  };
  const section4TopRightData = ionHealthcareSection4Data[1] as {
    subheading: string;
    body: string;
  };
  const section4BottomRightData = ionHealthcareSection4Data[2] as {
    subheading: string;
    body: string;
  };
  const section4ImageData = ionHealthcareSection4Data[3] as {
    gallery: { url: string; alt: string }[];
  };

  const section5HeadingData = ionHealthcareSection5Data[0] as {
    heading: string;
    body: string;
  };
  const section5TopLeftData = ionHealthcareSection5Data[1] as {
    subheading: string;
    body: string;
  };
  const section5BottomLeftData = ionHealthcareSection5Data[2] as {
    subheading: string;
    body: string;
  };
  const section5ImageData = ionHealthcareSection5Data[3] as unknown as {
    gallery: { url: string; alt: string }[];
  };

  const section6HeadingData = ionHealthcareSection6Data[0] as {
    heading: string;
    body: string;
  };
  const section6TopRightData = ionHealthcareSection6Data[1] as {
    subheading: string;
    body: string;
  };
  const section6BottomRightData = ionHealthcareSection6Data[2] as {
    subheading: string;
    body: string;
  };
  const section6ImageData = ionHealthcareSection6Data[3] as unknown as {
    gallery: { url: string; alt: string }[];
  };

  const section7HeadingData = ionHealthcareSection7Data[0] as {
    heading: string;
    body: string;
  };
  const section7ImageSubheadingData = ionHealthcareSection7Data.slice(1, 4) as Array<{
    subheading: string;
    body: string;
    gallery: { url: string; alt: string }[];
  }>;
  const section7Body2Data = ionHealthcareSection7Data[4] as {
    body2: string;
  };

  const section8HeadingData = ionHealthcareSection8Data[0] as {
    heading: string;
    body: string;
  };
  const section8SubheadingData = ionHealthcareSection8Data[1] as {
    subheading: string;
    body: string;
  };
  const section8SubheadingData2 = ionHealthcareSection8Data[2] as {
    subheading: string;
    body: string;
  };
  const section8ImageData = ionHealthcareSection8Data[3] as {
    gallery: { url: string; alt: string }[];
  };

  const section9HeadingData = ionHealthcareSection9Data[0] as {
    heading: string;
    gallery: { url: string; alt: string }[];
  };
  const section9FaqItems = ionHealthcareSection9Data.slice(1) as Array<{
    subheading: string;
    body: string;
  }>;
  const section9QuestionIcons = section9HeadingData.gallery ?? [];

  const sectionAccordionItems: Record<
    AccordionSectionId,
    Array<{
      id: string;
      subheading: string;
      body: string;
    }>
  > = {
    section4: [
      {
        id: "section4-top",
        subheading: section4TopRightData.subheading,
        body: section4TopRightData.body,
      },
      {
        id: "section4-bottom",
        subheading: section4BottomRightData.subheading,
        body: section4BottomRightData.body,
      },
    ],
    section5: [
      {
        id: "section5-top",
        subheading: section5TopLeftData.subheading,
        body: section5TopLeftData.body,
      },
      {
        id: "section5-bottom",
        subheading: section5BottomLeftData.subheading,
        body: section5BottomLeftData.body,
      },
    ],
    section6: [
      {
        id: "section6-top",
        subheading: section6TopRightData.subheading,
        body: section6TopRightData.body,
      },
      {
        id: "section6-bottom",
        subheading: section6BottomRightData.subheading,
        body: section6BottomRightData.body,
      },
    ],
  };

  const toggleSectionAccordion = (sectionId: AccordionSectionId, index: number) => {
    setAccordionActiveIndices((current) => ({
      ...current,
      [sectionId]: current[sectionId] === index ? null : index,
    }));
  };

  const renderDesktopGroup = (sectionId: AccordionSectionId) => (
    <div className={styles.subheadingBodyContainer}>
      {sectionAccordionItems[sectionId].map((item) => (
        <div key={item.id} className={styles.subheadingBodyInnerContainer}>
          <h4>{item.subheading}</h4>
          <ReactMarkdown>{item.body}</ReactMarkdown>
        </div>
      ))}
    </div>
  );

  const renderAccordionGroup = (sectionId: AccordionSectionId) => (
    <div className={styles.subheadingAccordionContainer}>
      {sectionAccordionItems[sectionId].map((item, index) => {
        const isActive = accordionActiveIndices[sectionId] === index;
        const panelId = `${item.id}-panel`;

        return (
          <div
            key={item.id}
            className={`${styles.subheadingBodyInnerContainer} ${
              styles.subheadingBodyInnerContainerAccordion
            } ${
              isActive
                ? styles.subheadingBodyInnerContainerActive
                : styles.subheadingBodyInnerContainerInactive
            }`}
          >
            <button
              type="button"
              className={styles.subheadingAccordionTrigger}
              onClick={() => toggleSectionAccordion(sectionId, index)}
              aria-expanded={isActive}
              aria-controls={panelId}
            >
              <h4>{item.subheading}</h4>
            </button>
            <div
              id={panelId}
              className={`${styles.subheadingAccordionBody} ${
                isActive ? styles.subheadingAccordionBodyActive : ""
              }`}
              aria-hidden={!isActive}
            >
              <div className={styles.subheadingAccordionBodyInner}>
                <ReactMarkdown>{item.body}</ReactMarkdown>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );

  const stickyContent = [
    {
      id: "section4",
      sectionClassName: styles.section4,
    },
    {
      id: "section5",
      sectionClassName: styles.section5,
    },
    {
      id: "section6",
      sectionClassName: styles.section6,
    },
  ];

  const transitionStickyPanel = (
    panels: HTMLDivElement[],
    nextPanelIndex: number
  ) => {
    const currentPanelIndex = stickyCurrentPanelIndexRef.current;

    if (nextPanelIndex === currentPanelIndex) {
      return;
    }

    gsap.to(panels[currentPanelIndex], {
      autoAlpha: 0,
      duration: 0.35,
      ease: "power2.out",
      overwrite: "auto",
    });

    gsap.fromTo(
      panels[nextPanelIndex],
      { autoAlpha: 0 },
      {
        autoAlpha: 1,
        duration: 0.45,
        ease: "power2.out",
        overwrite: "auto",
      }
    );

    stickyCurrentPanelIndexRef.current = nextPanelIndex;
    setStickyActiveIndex(nextPanelIndex);
  };

  const scrollToStickyPanel = (index: number) => {
    const step = stickyStepsRef.current[index];
    const wrapper = wrapperRef.current;
    if (!step || !wrapper) {
      return;
    }

    const wrapperTop = wrapper.getBoundingClientRect().top + window.scrollY;
    const stepTop = step.getBoundingClientRect().top + window.scrollY;
    const preferredTarget = index === 0
      ? wrapperTop
      : stepTop - window.innerHeight * 0.25;
    const targetY = Math.max(wrapperTop, preferredTarget);

    const panels = stickyPanelsRef.current.filter(
      (panel): panel is HTMLDivElement => panel !== null
    );

    if (!panels.length) {
      return;
    }

    if (stickyJumpDelayTimerRef.current !== null) {
      window.clearTimeout(stickyJumpDelayTimerRef.current);
      stickyJumpDelayTimerRef.current = null;
    }
    stickyScrollTweenRef.current?.kill();
    stickyJumpingRef.current = true;
    stickyJumpTargetRef.current = index;

    stickyJumpDelayTimerRef.current = window.setTimeout(() => {
      stickyJumpDelayTimerRef.current = null;
      transitionStickyPanel(panels, index);
    }, 500);

    const state = { y: window.scrollY };
    stickyScrollTweenRef.current = gsap.to(state, {
      y: targetY,
      duration: 0.55,
      ease: "power2.out",
      overwrite: true,
      onUpdate: () => {
        window.scrollTo(0, state.y);
      },
      onComplete: () => {
        stickyJumpingRef.current = false;
        stickyJumpTargetRef.current = null;
        stickyScrollTweenRef.current = null;
      },
      onInterrupt: () => {
        stickyJumpingRef.current = false;
        stickyJumpTargetRef.current = null;
        stickyScrollTweenRef.current = null;
      },
    });
  };

  useLayoutEffect(() => {
    if (!wrapperRef.current || !stickyRef.current || stickyContent.length <= 1) return;

    const panels = stickyPanelsRef.current.filter(
      (panel): panel is HTMLDivElement => panel !== null
    );
    const steps = stickyStepsRef.current.filter(
      (step): step is HTMLDivElement => step !== null
    );

    if (!panels.length || !steps.length) return;

    const showPanel = (nextPanelIndex: number) => {
      if (nextPanelIndex === stickyCurrentPanelIndexRef.current) {
        return;
      }

      if (stickyJumpingRef.current) {
        return;
      }

      transitionStickyPanel(panels, nextPanelIndex);
    };

    const ctx = gsap.context(() => {
      gsap.set(panels, { autoAlpha: 0 });
      gsap.set(panels[0], { autoAlpha: 1 });
      setStickyActiveIndex(0);
      stickyCurrentPanelIndexRef.current = 0;

      ScrollTrigger.create({
        trigger: wrapperRef.current,
        start: "top top",
        end: "bottom bottom",
        pin: stickyRef.current,
        id: "section4-6-pin",
        pinSpacing: false,
        anticipatePin: 1,
        invalidateOnRefresh: true,
        onLeaveBack: () => {
          if (stickyJumpingRef.current) return;
          showPanel(0);
        },
      });

      steps.forEach((step, index) => {
        ScrollTrigger.create({
          trigger: step,
          start: "top 25%",
          end: "top 25%",
          id: stickyContent[index].id,
          onEnter: () => {
            showPanel(index);
          },
          onEnterBack: () => {
            showPanel(index);
          },
        });
      });
    }, wrapperRef);

    return () => {
      if (stickyJumpDelayTimerRef.current !== null) {
        window.clearTimeout(stickyJumpDelayTimerRef.current);
        stickyJumpDelayTimerRef.current = null;
      }
      stickyScrollTweenRef.current?.kill();
      stickyScrollTweenRef.current = null;
      ctx.revert();
    };
  }, [stickyContent.length]);


  return (
    <div id="IonHealthcareID">
      <div className={styles.gradientBkgd}></div>

      {/* ---------- Section 1 : Healthcare ---------- */}
      <section className={styles.section1}>
        <div className={`${styles.maxWidthContainer} maxWidthContainer`}>
          <div className={styles.copyContainer}>
            {/* <motion.span
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: "easeOut" }}
            >
              {parse(ionHealthcareSection1Data.overline)}
            </motion.span> */}
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2, ease: "easeOut" }}
            >
              {parse(ionHealthcareSection1Data.heading)}
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4, ease: "easeOut" }}
            >
              {parse(ionHealthcareSection1Data.body)}
            </motion.p>
            <MotionTransitionLink
              href="/contact-us"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.5, ease: "linear" }}
            >
              {parse(ionHealthcareSection1Data.button)}
            </MotionTransitionLink>
          </div>
          <div className={styles.imgContainer}>
            <MotionImage
              src={ionHealthcareSection1Data.gallery[0].url}
              alt={ionHealthcareSection1Data.gallery[0].alt}
              width={500}
              height={500}
              priority
              fetchPriority="high"
              loading="eager"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6, ease: "easeOut" }}
            />
          </div>
        </div>
      </section>


      {/* ---------- Section 2 :  ---------- */}
      <section className={styles.section2}>
        <div className={`${styles.maxWidthContainer} maxWidthContainer`}>
          <div className={styles.imgContainer}>
            <MotionImage
              src={ionHealthcareSection2Data.gallery[0].url}
              alt={ionHealthcareSection2Data.gallery[0].alt}
              width={500}
              height={500}
              priority
              fetchPriority="high"
              loading="eager"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.6, delay: 0.0, ease: "easeOut" }}
            />
          </div>
          <div className={styles.copyContainer}>
            {/* <motion.span
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: "easeOut" }}
            >
              {parse(ionHealthcareSection2Data.overline)}
            </motion.span>
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2, ease: "easeOut" }}
            >
              {parse(ionHealthcareSection2Data.heading)}
            </motion.h1> */}
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.6, delay: 0.4, ease: "easeOut" }}
            >
              {parse(ionHealthcareSection2Data.body)}
            </motion.p>
          </div>
        </div>
      </section>


      {/* ---------- Section 3 : Key Solutions For Healthcare ---------- */}
      <section ref={section3Ref} className={styles.section3}>
        <div className={`${styles.maxWidthContainer} maxWidthContainer`}>
          <div className={styles.copyContainer}>
            <p className={styles.overline}>
              {parse(ionHealthcareSection3Data.overline)}
            </p>
            <h2>{parse(ionHealthcareSection3Data.heading)}</h2>
          </div>

          <div className={styles.svgArrowWrap}>
            <Image
              className={styles.svgArrowBase}
              src={ionHealthcareSection3Data.gallery[0].url}
              alt={ionHealthcareSection3Data.gallery[0].alt}
              width={10}
              height={100}
            />
            <MotionImage
              className={styles.svgArrowDraw}
              src={ionHealthcareSection3Data.gallery[0].url}
              alt={ionHealthcareSection3Data.gallery[0].alt}
              width={10}
              height={100}
              initial={{ opacity: 1, clipPath: "inset(0 0 100% 0)" }}
              animate={{
                opacity: 1,
                clipPath: [
                  "inset(0 0 100% 0)",
                  "inset(0 0 0% 0)",
                  "inset(100% 0 0% 0)",
                ],
              }}
              transition={{
                duration: 1.8,
                ease: "linear",
                repeat: Infinity,
                repeatDelay: 0.2,
                times: [0, 0.5, 1],
                repeatType: "loop",
              }}
            />
          </div>
        </div>
      </section>


      {/* ---------- Section 4 - 6 : Real-Time Patient Monitoring ---------- */}
      <section
        ref={wrapperRef}
        className={`${styles.stickyScrollWrapper} ${styles.stickySection}`}
        style={{ ["--sticky-panels" as string]: stickyContent.length }}
      >
        <div className={styles.stickyStepsTrack}>
          {stickyContent.map((item, index) => (
            <div
              key={`${item.id}-step`}
              ref={(element) => {
                stickyStepsRef.current[index] = element;
              }}
              className={styles.stickyStep}
              aria-hidden="true"
            />
          ))}
          <div className={styles.stickyStepTail} aria-hidden="true" />
        </div>

        <div ref={stickyRef} className={styles.stickyContent}>
          <div className={`${styles.maxWidthContainer} maxWidthContainer`}>
            <div className={styles.pagination}>
              {stickyContent.map((item, index) => (
                <button
                  key={item.id}
                  type="button"
                  className={`${styles.paginationItem} ${
                    index <= stickyActiveIndex
                      ? styles.pastPaginationItem
                      : styles.upcomingPaginationItem
                  }`}
                  onClick={() => scrollToStickyPanel(index)}
                  aria-label={`Scroll to panel ${index + 1}`}
                />
              ))}
            </div>

            <div className={styles.contentStage}>
              {stickyContent.map((item, index) => (
                <div
                  key={item.id}
                  ref={(element) => {
                    stickyPanelsRef.current[index] = element;
                  }}
                  className={`${styles.stickyPanel} ${item.sectionClassName} ${
                    index === stickyActiveIndex ? styles.stickyPanelActive : ""
                  }`}
                  aria-hidden={index !== stickyActiveIndex}
                >
                  {item.id === "section4" && (
                    <div className={`${styles.maxWidthContainer} maxWidthContainer`}>
                      <div className={styles.contentStage}>
                        <div className={styles.headingBodyContainer}>
                          <span>Use Cases</span>
                          <h2>
                            {/* Safer Campuses Through Real-Time Awareness */}
                            {section4HeadingData.heading}
                          </h2>
                          <p>
                            {section4HeadingData.body}
                          </p>
                        </div>
                        <div className={styles.subheadingBodyImgContainer}>
                          <div className={styles.imgContainer}>
                            <MotionImage
                              src={section4ImageData.gallery[0].url}
                              alt={section4ImageData.gallery[0].alt}
                                fill
                                priority
                                fetchPriority="high"
                              loading="eager"
                              initial={{ opacity: 0, y: 30 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ duration: 0.6, delay: 0.6, ease: "easeOut" }}
                            />
                          </div>
                          {renderDesktopGroup("section4")}
                          {renderAccordionGroup("section4")}
                        </div>
                      </div>
                    </div>
                  )}

                  {item.id === "section5" && (
                    <div className={`${styles.maxWidthContainer} maxWidthContainer`}>
                      <div className={styles.contentStage}>
                        <div className={styles.headingBodyContainer}>
                          <span>Use Cases</span>
                          <h2>
                            {/* Safer Campuses Through Real-Time Awareness */}
                            {section5HeadingData.heading}
                          </h2>
                          <p>
                            {section5HeadingData.body}
                          </p>
                        </div>
                        <div className={styles.subheadingBodyImgContainer}>
                          {renderDesktopGroup("section5")}
                          {renderAccordionGroup("section5")}

                          <div className={styles.imgContainer}>
                            <MotionImage
                              src={section5ImageData.gallery[0].url}
                              alt={section5ImageData.gallery[0].alt}
                              fill
                              priority
                              fetchPriority="high"
                              loading="eager"
                              initial={{ opacity: 0, y: 30 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ duration: 0.6, delay: 0.6, ease: "easeOut" }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {item.id === "section6" && (
                    <div className={`${styles.maxWidthContainer} maxWidthContainer`}>
                      <div className={styles.contentStage}>
                        <div className={styles.headingBodyContainer}>
                          <span>Use Cases</span>
                          <h2>
                            {/* Safer Campuses Through Real-Time Awareness */}
                            {section6HeadingData.heading}
                          </h2>
                          <p>
                            {section6HeadingData.body}
                          </p>
                        </div>
                        <div className={styles.subheadingBodyImgContainer}>
                          <div className={styles.imgContainer}>
                            <MotionImage
                              src={section6ImageData.gallery[0].url}
                              alt={section6ImageData.gallery[0].alt}
                              fill
                              priority
                              fetchPriority="high"
                              loading="eager"
                              initial={{ opacity: 0, y: 30 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ duration: 0.6, delay: 0.6, ease: "easeOut" }}
                            />
                          </div>
                          {renderDesktopGroup("section6")}
                          {renderAccordionGroup("section6")}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>


      {/* ---------- Section 7 : The Complete Tech Stack ---------- */}
      <section className={styles.section7}>
        <div className={`${styles.maxWidthContainer} maxWidthContainer`}>
          <div className={styles.headingBodyContainer}>
            <h2>
              {parse(section7HeadingData.heading)}
            </h2>
            <p>
              {parse(section7HeadingData.body)}
            </p>
          </div>

          {section7ImageSubheadingData.map((item, index) => (
            <div className={styles.imgSubheadingBodyContainer} key={index}>
              <div className={styles.imgSubheadingBodyInnerContainer}>
                <Image
                  src={item.gallery[0].url}
                  alt={item.gallery[0].alt}
                  width={75}
                  height={75}
                  loading="eager"
                />
                <div className={styles.subheadingBodyContainer}>
                  <h3>{item.subheading}</h3>
                  <p>{parse(item.body)}</p>
                </div>
              </div>
            </div>
          ))}

          <div className={styles.body2Container}>
            <p>{parse(section7Body2Data.body2)}</p>
          </div>
        </div>
      </section>


      {/* ---------- Section 8 : Partnership Section ---------- */}
      <section className={styles.section8}>
        <div className={`${styles.maxWidthContainer} maxWidthContainer`}>
          <div className={styles.contentStage}>
            {/* <div className={styles.headingBodyContainer}>
              <h2>
                {ionHealthcareSection8Data[0].heading}
              </h2>
              <p>
                {ionHealthcareSection8Data[0].body}
              </p>
            </div> */}
            <div className={styles.subheadingBodyImgContainer}>
              <div className={styles.imgContainer}>
                <MotionImage
                  src={section8ImageData.gallery[0].url}
                  alt={section8ImageData.gallery[0].alt}
                  fill
                  // priority
                  // fetchPriority="high"
                  loading="eager"
                  initial={{ opacity: 0, x: -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-100px" }}
                  transition={{ duration: 0.6, ease: "easeOut" }}
                />
              </div>

              <motion.div
                className={styles.subheadingBodyContainer}
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.6, ease: "easeOut" }}
              >
                <div className={styles.subheadingBodyInnerContainer}>
                  <h2>{section8HeadingData.heading}</h2>
                  <ReactMarkdown>
                    {section8HeadingData.body}
                  </ReactMarkdown>
                </div>

                <div className={styles.subheadingBodyInnerContainer}>
                  <h3>{section8SubheadingData.subheading}</h3>
                  <ReactMarkdown>
                    {section8SubheadingData.body}
                  </ReactMarkdown>
                </div>
                <div className={styles.subheadingBodyInnerContainer}>
                  <h3>{section8SubheadingData2.subheading}</h3>
                  <ReactMarkdown>
                    {section8SubheadingData2.body}
                  </ReactMarkdown>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>


      {/* ---------- Section 9 : FAQ ---------- */}
      <section ref={section9Ref} className={styles.section9}>
        <div className={`${styles.maxWidthContainer} maxWidthContainer`}>
          <div className={styles.headingContainer}>
            <h2>
              {parse(section9HeadingData.heading)}
            </h2>
          </div>

          <div className={styles.faqsContainer}>
            {section9FaqItems.map((item, index) => {
              const isActive = index === faqActiveIndex;
              const icon = isActive
                ? section9QuestionIcons[1] ?? section9QuestionIcons[0]
                : section9QuestionIcons[0];

              return (
                <div
                  key={`${item.subheading}-${index}`}
                  className={`${styles.questionAnswerContainer} ${
                    isActive ? styles.active : ""
                  }`}
                >
                  <button
                    type="button"
                    className={styles.imgQuestionContainer}
                    onClick={() => setFaqActiveIndex(index)}
                    aria-expanded={isActive}
                    aria-controls={`section9-answer-${index}`}
                  >
                    <span className={styles.questionArrow}>
                      {icon ? (
                        <Image
                          src={icon.url}
                          alt={icon.alt}
                          width={15}
                          height={15}
                        />
                      ) : null}
                    </span>
                    <p>{parse(item.subheading)}</p>
                  </button>
                  <div
                    id={`section9-answer-${index}`}
                    className={styles.answerContainer}
                    aria-hidden={!isActive}
                  >
                    <div className={styles.answerInner}>
                      <p>{parse(item.body)}</p>
                    </div>
                  </div>
                </div>
              );
            })}


          </div>
        </div>
      </section>
    </div>
  );
};

export default HealthcareView;
